import { CATEGORIES, BlockStatus, type Category } from '../model/enums';
import { isLive, type ProjectRecord, type TimeBlockRecord } from '../model/types';
import { effectiveCategory, indexProjects } from '../model/project-tree';
import { daysBetween } from '../time/date';
import { HOURS_PER_DAY, MINUTES_PER_DAY } from '../model/constants';
import { confirmedBlocksIn, resolveSegments, type DateRange } from './segments';

export interface Summary {
  readonly totalMin: number;
  readonly byProject: Readonly<Record<string, number>>;
  readonly byCategory: Readonly<Record<Category, number>>;
  /** Minutes of blocks whose project is missing or deleted; they are not in `byCategory`. */
  readonly uncategorizedMin: number;
}

function emptyCategoryTotals(): Record<Category, number> {
  return Object.fromEntries(CATEGORIES.map((category) => [category, 0])) as Record<Category, number>;
}

function summarizeSegments(
  blocks: readonly TimeBlockRecord[],
  projects: readonly ProjectRecord[],
  range?: DateRange,
): Summary {
  const byId = indexProjects(projects);
  const categoryOf = new Map([...byId].map(([id, project]) => [id, effectiveCategory(project, byId)]));
  const byProject: Record<string, number> = {};
  const byCategory = emptyCategoryTotals();
  let totalMin = 0;
  let uncategorizedMin = 0;
  for (const segment of resolveSegments(blocks, range)) {
    const minutes = segment.endAbs - segment.startAbs;
    totalMin += minutes;
    byProject[segment.projectId] = (byProject[segment.projectId] ?? 0) + minutes;
    const category = categoryOf.get(segment.projectId);
    if (category === undefined) uncategorizedMin += minutes;
    else byCategory[category] += minutes;
  }
  return { totalMin, byProject, byCategory, uncategorizedMin };
}

/**
 * Minutes per project and category over `range` (inclusive). Only confirmed, live blocks
 * count; overlapping minutes are counted once, for the block with the greater hlc.
 */
export function summarize(
  blocks: readonly TimeBlockRecord[],
  projects: readonly ProjectRecord[],
  range: DateRange,
): Summary {
  return summarizeSegments(blocks, projects, range);
}

/** Minutes per wall-clock hour of day (24 buckets, index 0 = 00:00-01:00), by the same rules as `summarize`. */
export function hourHistogram(blocks: readonly TimeBlockRecord[], range: DateRange): number[] {
  const buckets = new Array<number>(HOURS_PER_DAY).fill(0);
  for (const segment of resolveSegments(blocks, range)) {
    let cursor = segment.startAbs;
    while (cursor < segment.endAbs) {
      const hourEnd = Math.floor(cursor / 60) * 60 + 60;
      const upTo = Math.min(segment.endAbs, hourEnd);
      const hour = Math.floor((((cursor % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY) / 60);
      buckets[hour] = (buckets[hour] ?? 0) + (upTo - cursor);
      cursor = upTo;
    }
  }
  return buckets;
}

export interface Completeness {
  /** Days in the range with at least one confirmed block. */
  readonly daysWithRecords: number;
  /** Days of the range that have begun: all of them, or up to `today` when given. */
  readonly totalDays: number;
  /** `daysWithRecords / totalDays`, 0 when no day of the range has begun. */
  readonly ratio: number;
}

/**
 * Share of the range's days that have at least one confirmed block. With `today` (a personal
 * date) days after it are left out of both counts: a day that has not come cannot be missing a
 * record, so a week in progress is not rated against its future days.
 */
export function completeness(
  blocks: readonly TimeBlockRecord[],
  range: DateRange,
  today?: string,
): Completeness {
  const to = today !== undefined && today < range.to ? today : range.to;
  const totalDays = Math.max(0, daysBetween(range.from, to) + 1);
  if (totalDays === 0) return { daysWithRecords: 0, totalDays: 0, ratio: 0 };
  const days = new Set(
    confirmedBlocksIn(blocks, { from: range.from, to }).map((block) => block.data.personalDate),
  );
  return { daysWithRecords: days.size, totalDays, ratio: days.size / totalDays };
}

export interface DayTotals extends Summary {
  /** Live drafts among the given blocks: the "待确认 N 项" counter. */
  readonly draftCount: number;
}

/**
 * Totals of one personal day: pass that day's blocks (the caller filters out expired drafts).
 * Drafts add to `draftCount` only, never to minutes.
 */
export function dayCategoryTotals(
  blocksOfOneDate: readonly TimeBlockRecord[],
  projects: readonly ProjectRecord[],
): DayTotals {
  const draftCount = blocksOfOneDate
    .filter(isLive)
    .filter((block) => block.data.status === BlockStatus.Draft).length;
  return { ...summarizeSegments(blocksOfOneDate, projects), draftCount };
}
